import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { DownloadSimple, Funnel, Trash, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { formatDate } from "@/lib/dateFmt";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DatePickerZoom from "@/components/DatePickerZoom";
import { speciesLabel } from "@/lib/patientUtils";

export default function ReportsPage() {
  const { t, lang } = useLang();
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    vet_id: "",
    visit_reason: "",
    species: "",
    diagnosis_id: "",
  });
  const [rows, setRows] = useState([]);
  const [vets, setVets] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [stats, setStats] = useState(null);
  const [groupBy, setGroupBy] = useState("diagnosis");

  async function load() {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null)
      );
      const [cRes, uRes, rRes, dRes] = await Promise.all([
        api.get("/consultations", { params }),
        api.get("/users"),
        api.get("/visit-reasons"),
        api.get("/diagnoses"),
      ]);
      // Filter rows by diagnosis_id client-side (backend consultations endpoint doesn't filter by it yet)
      let all = cRes.data;
      if (filters.diagnosis_id) {
        all = all.filter((c) => (c.diagnosis_ids || []).includes(filters.diagnosis_id));
      }
      setRows(all);
      setVets(uRes.data);
      setReasons(rRes.data);
      setDiagnoses(dRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function loadStats() {
    try {
      const params = { group_by: groupBy };
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.vet_id) params.vet_id = filters.vet_id;
      if (filters.species) params.species = filters.species;
      if (filters.diagnosis_id) params.diagnosis_id = filters.diagnosis_id;
      const { data } = await api.get("/diagnoses/stats", { params });
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line
  }, [filters, groupBy]);

  async function exportXlsx() {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== "diagnosis_id")
      );
      params.lang = lang;
      const res = await api.get("/reports/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pawfile-consultations.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteRow(id) {
    if (!window.confirm("Delete this consultation from records? This cannot be undone.")) return;
    try {
      await api.delete(`/consultations/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function upd(k, v) {
    if (k === "to_date" && filters.from_date && v && v < filters.from_date) {
      toast.error(t("invalidDateRange"));
      return;
    }
    if (k === "from_date" && filters.to_date && v && filters.to_date < v) {
      toast.error(t("invalidDateRange"));
      setFilters((f) => ({ ...f, from_date: v, to_date: "" }));
      return;
    }
    setFilters((f) => ({ ...f, [k]: v }));
  }
  function reset() {
    setFilters({ from_date: "", to_date: "", vet_id: "", visit_reason: "", species: "", diagnosis_id: "" });
  }

  const maxStat = Math.max(1, ...((stats?.rows || []).map((r) => r.count)));

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-pf-text">{t("reports")}</h1>
        </div>
        <button data-testid="export-xlsx-btn" onClick={exportXlsx} className="pf-btn-primary">
          <DownloadSimple size={14} /> {t("exportXlsx")}
        </button>
      </div>

      <div className="pf-card p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 pf-label">
            <Funnel size={14} /> {t("filters")}
          </div>
          <button onClick={reset} className="text-xs text-pf-primary hover:underline" data-testid="reset-filters-btn">
            {t("reset")}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="pf-label mb-1 block">{t("dateFrom")}</label>
            <DatePickerZoom testId="filter-from" value={filters.from_date} onChange={(v) => upd("from_date", v)} />
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("dateTo")}</label>
            <DatePickerZoom testId="filter-to" value={filters.to_date} onChange={(v) => upd("to_date", v)} />
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("veterinarian")}</label>
            <select data-testid="filter-vet" className="pf-input" value={filters.vet_id} onChange={(e) => upd("vet_id", e.target.value)}>
              <option value="">{t("all")}</option>
              {vets.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("visitReasons")}</label>
            <select data-testid="filter-reason" className="pf-input" value={filters.visit_reason} onChange={(e) => upd("visit_reason", e.target.value)}>
              <option value="">{t("all")}</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.label}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("species")}</label>
            <select data-testid="filter-species" className="pf-input" value={filters.species} onChange={(e) => upd("species", e.target.value)}>
              <option value="">{t("all")}</option>
              <option value="dog">{t("dog")}</option>
              <option value="cat">{t("cat")}</option>
              <option value="other">{t("other")}</option>
            </select>
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("filterDiagnosis")}</label>
            <select data-testid="filter-diagnosis" className="pf-input" value={filters.diagnosis_id} onChange={(e) => upd("diagnosis_id", e.target.value)}>
              <option value="">{t("all")}</option>
              {diagnoses.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="bg-white border border-pf-border rounded-lg p-1">
          <TabsTrigger value="list" data-testid="tab-reports-list">{t("consultations")}</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-reports-stats">
            <ChartBar size={14} className="mr-1" />{t("diagnosisStats")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <div className="pf-card overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-pf-bg text-pf-text-secondary">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">{t("date")}</th>
                  <th className="text-left font-semibold px-4 py-3">{t("name")}</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("owner")}</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("attendingVet")}</th>
                  <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">{t("diagnosis")}</th>
                  <th className="text-left font-semibold px-4 py-3">{t("invoiceStatus")}</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-pf-text-secondary py-10">
                      {t("noResults")}
                    </td>
                  </tr>
                )}
                {rows.map((c) => (
                  <tr key={c.id} className="border-t border-pf-border hover:bg-pf-bg transition-colors" data-testid={`report-row-${c.id}`}>
                    <td className="px-4 py-3">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/consultations/${c.id}`} className="font-semibold text-pf-primary hover:underline">
                        {c.patient_name || "—"}
                      </Link>
                      <div className="text-xs text-pf-text-secondary">{speciesLabel(c.patient_species, t)}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">{c.owner_name || "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{c.attending_vet_name || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-pf-text-secondary">
                      {c.diagnosis_ids && c.diagnosis_ids.length > 0
                        ? c.diagnosis_ids.map((id) => diagnoses.find((d) => d.id === id)?.name).filter(Boolean).join(", ")
                        : (c.diagnosis || "—")}
                    </td>
                    <td className="px-4 py-3">
                      {c.status === "draft" ? (
                        <span className="pf-chip text-pf-warning bg-pf-warning-bg border-pf-warning/30">draft</span>
                      ) : (
                        <span className="pf-chip-primary">final</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`report-delete-${c.id}`}
                        onClick={() => deleteRow(c.id)}
                        className="p-1.5 rounded text-pf-text-secondary hover:text-pf-danger hover:bg-pf-danger-bg"
                        title={t("delete")}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="stats" className="mt-4">
          <div className="pf-card p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="pf-section-title">{t("diagnosisStats")}</div>
              <div className="flex items-center gap-2 text-sm">
                <span className="pf-label">{t("groupBy")}:</span>
                <select
                  data-testid="stats-groupby"
                  className="pf-input !py-1.5 max-w-[200px]"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <option value="diagnosis">{t("groupByDiagnosis")}</option>
                  <option value="month">{t("groupByMonth")}</option>
                  <option value="species">{t("groupBySpecies")}</option>
                  <option value="vet">{t("groupByVet")}</option>
                </select>
              </div>
            </div>
            <div className="text-sm text-pf-text-secondary mb-3">
              {t("totalCases")}: <span className="font-semibold text-pf-text" data-testid="stats-total">{stats?.total_consultations ?? 0}</span>
            </div>
            {(!stats?.rows || stats.rows.length === 0) ? (
              <div className="text-sm text-pf-text-secondary py-8 text-center">{t("noResults")}</div>
            ) : (
              <div className="space-y-2">
                {stats.rows.map((r) => (
                  <div key={r.key} data-testid={`stats-row-${r.key}`} className="flex items-center gap-3">
                    <div className="w-48 truncate text-sm">{groupBy === "species" ? speciesLabel(r.label, t) : r.label}</div>
                    <div className="flex-1 h-6 bg-pf-bg rounded overflow-hidden">
                      <div
                        className="h-full bg-pf-primary transition-all"
                        style={{ width: `${(r.count / maxStat) * 100}%` }}
                      />
                    </div>
                    <div className="w-12 text-right text-sm font-semibold text-pf-primary">{r.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
